/**
 * Legacy roll-ups kept for the pricing suggester. Reads the real ledger
 * metadata keys (`usage`, `tool_calls`, `tool_names`, `model`) via
 * `parseTurnMetadata`, with fallbacks for the older key names.
 */
import { parseTurnMetadata } from './telemetryTypes';

export interface LedgerRow {
  id: string;
  created_at: string;
  kind: string;
  turns: number;
  credit_equivalent: number;
  project_id: string | null;
  thread_id: string | null;
  metadata: Record<string, unknown> | null;
}

export type ToolUsage = { tool: string; count: number };

function num(x: unknown, fallback = 0): number {
  return typeof x === 'number' && Number.isFinite(x) ? x : fallback;
}

export function summarize(rows: LedgerRow[]) {
  const turnRows = rows.filter((r) => r.kind === 'turn_debit');
  const totals = {
    turns: turnRows.length,
    iterations: 0,
    toolCalls: 0,
    promptTokens: 0,
    completionTokens: 0,
    totalTokens: 0,
    durationMs: 0,
  };
  const toolCounts = new Map<string, number>();

  for (const r of turnRows) {
    const m = parseTurnMetadata(r.metadata);
    totals.iterations += m.iterations;
    totals.toolCalls += m.toolCalls;
    totals.promptTokens += m.tokensIn;
    totals.completionTokens += m.tokensOut + m.tokensThinking;
    totals.totalTokens += m.tokensTotal;
    totals.durationMs += m.durationMs;
    for (const t of m.toolNames) toolCounts.set(t, (toolCounts.get(t) ?? 0) + 1);
  }

  const topTools: ToolUsage[] = [...toolCounts.entries()]
    .map(([tool, count]) => ({ tool, count }))
    .sort((a, b) => b.count - a.count);

  const avg = {
    iterationsPerTurn: totals.turns ? totals.iterations / totals.turns : 0,
    toolCallsPerTurn: totals.turns ? totals.toolCalls / totals.turns : 0,
    totalTokensPerTurn: totals.turns ? totals.totalTokens / totals.turns : 0,
    durationMsPerTurn: totals.turns ? totals.durationMs / totals.turns : 0,
  };

  return { totals, avg, topTools };
}

export interface RepairSummary {
  attempts: number;
  layer0Attempts: number;
  layer1Attempts: number;
  layer0Applied: number;
  layer1Applied: number;
  layer1TokensIn: number;
  layer1TokensOut: number;
  layer1AvgTokens: number;
  layer0HitRate: number;
  layer1HitRate: number;
  /** Codes Layer 1 attempted but could not fix — candidates for a Layer 0 fixer. */
  unresolvedCodes: Array<{ code: string; count: number }>;
}

export function summarizeRepairs(rows: LedgerRow[]): RepairSummary {
  const repairs = rows.filter((r) => r.kind === 'auto_repair');
  let l0 = 0, l1 = 0, l0Applied = 0, l1Applied = 0, tIn = 0, tOut = 0;
  const unresolved = new Map<string, number>();
  for (const r of repairs) {
    const m = (r.metadata ?? {}) as Record<string, unknown>;
    const isLayer1 = num(m.layer) === 1;
    if (isLayer1) {
      l1 += 1;
      if (m.applied === true) l1Applied += 1;
      else {
        const codes = Array.isArray(m.codes) ? m.codes : [];
        for (const c of codes) {
          if (typeof c !== 'string') continue;
          unresolved.set(c, (unresolved.get(c) ?? 0) + 1);
        }
      }
      tIn += num(m.tokens_in);
      tOut += num(m.tokens_out);
    } else {
      l0 += 1;
      // Layer 0 only inserts a ledger row when it applied fixes.
      l0Applied += 1;
    }
  }
  return {
    attempts: repairs.length,
    layer0Attempts: l0,
    layer1Attempts: l1,
    layer0Applied: l0Applied,
    layer1Applied: l1Applied,
    layer1TokensIn: tIn,
    layer1TokensOut: tOut,
    layer1AvgTokens: l1 ? (tIn + tOut) / l1 : 0,
    layer0HitRate: l0 ? l0Applied / l0 : 0,
    layer1HitRate: l1 ? l1Applied / l1 : 0,
    unresolvedCodes: Array.from(unresolved, ([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count),
  };
}
